import api from './api';

class PatrimonioService {
  /**
   * Listar patrimônios com filtros e paginação
   */
  async listar(filtros = {}) {
    const params = new URLSearchParams();

    if (filtros.search) params.append('search', filtros.search);
    if (filtros.localId) params.append('localId', filtros.localId);
    if (filtros.obraId) params.append('obraId', filtros.obraId);
    if (filtros.categoriaId) params.append('categoriaId', filtros.categoriaId);
    if (filtros.tipo) params.append('tipo', filtros.tipo);
    if (filtros.situacao) params.append('situacao', filtros.situacao);
    if (filtros.responsavel) params.append('responsavel', filtros.responsavel);
    if (filtros.page) params.append('page', filtros.page);
    if (filtros.pageSize) params.append('pageSize', filtros.pageSize);

    const response = await api.get(`/patrimonios?${params.toString()}`);
    return response.data; // { records, total, page, pageSize }
  }

  /**
   * Buscar patrimônio por ID (com movimentações, manutenções, anexos e depreciação)
   */
  async buscarPorId(id) {
    const response = await api.get(`/patrimonios/${id}`);
    return response.data;
  }

  /**
   * Próximo código disponível (preview para o formulário)
   */
  async proximoCodigo() {
    const response = await api.get('/patrimonios/proximo-codigo');
    return response.data; // { codigo }
  }

  /**
   * Criar patrimônio (FormData: campos + fotos/anexos iniciais)
   */
  async criar(formData) {
    const response = await api.post('/patrimonios', formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Atualizar patrimônio (FormData; localização só muda via movimentar)
   */
  async atualizar(id, formData) {
    const response = await api.put(`/patrimonios/${id}`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Excluir patrimônio
   */
  async excluir(id) {
    const response = await api.delete(`/patrimonios/${id}`);
    return response.data;
  }

  /**
   * Movimentar patrimônio (transferência de local/obra/responsável)
   */
  async movimentar(id, dados) {
    const response = await api.post(`/patrimonios/${id}/movimentar`, dados);
    return response.data; // { movimentacao, patrimonio }
  }

  /**
   * Dar baixa no patrimônio (doação, venda, descarte, extravio)
   */
  async darBaixa(id, dados) {
    const response = await api.post(`/patrimonios/${id}/baixa`, dados);
    return response.data;
  }

  /**
   * Reverter a baixa do patrimônio (volta para ATIVO)
   */
  async reverterBaixa(id, dados = {}) {
    const response = await api.post(`/patrimonios/${id}/reverter-baixa`, dados);
    return response.data;
  }

  /**
   * Histórico de movimentações do patrimônio
   */
  async listarMovimentacoes(id, filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.page) params.append('page', filtros.page);
    if (filtros.pageSize) params.append('pageSize', filtros.pageSize);

    const response = await api.get(`/patrimonios/${id}/movimentacoes?${params.toString()}`);
    return response.data;
  }

  /**
   * Criar manutenção do patrimônio
   */
  async criarManutencao(id, dados) {
    const response = await api.post(`/patrimonios/${id}/manutencoes`, dados);
    return response.data;
  }

  /**
   * Atualizar manutenção
   */
  async atualizarManutencao(id, manutencaoId, dados) {
    const response = await api.put(`/patrimonios/${id}/manutencoes/${manutencaoId}`, dados);
    return response.data;
  }

  /**
   * Excluir manutenção
   */
  async excluirManutencao(id, manutencaoId) {
    const response = await api.delete(`/patrimonios/${id}/manutencoes/${manutencaoId}`);
    return response.data;
  }

  /**
   * Upload de anexos/fotos do patrimônio (FormData)
   */
  async uploadAnexos(id, formData) {
    const response = await api.post(`/patrimonios/${id}/anexos`, formData, {
      headers: { 'Content-Type': 'multipart/form-data' },
    });
    return response.data;
  }

  /**
   * Excluir anexo do patrimônio
   */
  async excluirAnexo(id, anexoId) {
    const response = await api.delete(`/patrimonios/${id}/anexos/${anexoId}`);
    return response.data;
  }

  /**
   * Relatório de inventário (records + totais + agrupamentos)
   */
  async relatorioInventario(filtros = {}) {
    const params = new URLSearchParams();

    if (filtros.search) params.append('search', filtros.search);
    if (filtros.localId) params.append('localId', filtros.localId);
    if (filtros.obraId) params.append('obraId', filtros.obraId);
    if (filtros.categoriaId) params.append('categoriaId', filtros.categoriaId);
    if (filtros.tipo) params.append('tipo', filtros.tipo);
    if (filtros.situacao) params.append('situacao', filtros.situacao);
    if (filtros.responsavel) params.append('responsavel', filtros.responsavel);

    const response = await api.get(`/patrimonios/relatorio-inventario?${params.toString()}`);
    return response.data; // { records, totais, porLocal, porTipo, porSituacao }
  }

  // ---------------------------------------------------------------------
  // Locais de Patrimônio
  // ---------------------------------------------------------------------

  /**
   * Listar locais de patrimônio
   */
  async listarLocais(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.ativo !== undefined && filtros.ativo !== '') params.append('ativo', filtros.ativo);

    const response = await api.get(`/locais-patrimonio?${params.toString()}`);
    return response.data;
  }

  /**
   * Criar local de patrimônio
   */
  async criarLocal(dados) {
    const response = await api.post('/locais-patrimonio', dados);
    return response.data;
  }

  /**
   * Atualizar local de patrimônio
   */
  async atualizarLocal(id, dados) {
    const response = await api.put(`/locais-patrimonio/${id}`, dados);
    return response.data;
  }

  /**
   * Excluir local de patrimônio
   */
  async excluirLocal(id) {
    const response = await api.delete(`/locais-patrimonio/${id}`);
    return response.data;
  }

  // ---------------------------------------------------------------------
  // Categorias de Patrimônio
  // ---------------------------------------------------------------------

  /**
   * Listar categorias de patrimônio (filtros: tipo, ativo)
   */
  async listarCategorias(filtros = {}) {
    const params = new URLSearchParams();
    if (filtros.tipo) params.append('tipo', filtros.tipo);
    if (filtros.ativo !== undefined && filtros.ativo !== '') params.append('ativo', filtros.ativo);

    const response = await api.get(`/categorias-patrimonio?${params.toString()}`);
    return response.data;
  }

  /**
   * Criar categoria de patrimônio
   */
  async criarCategoria(dados) {
    const response = await api.post('/categorias-patrimonio', dados);
    return response.data;
  }

  /**
   * Atualizar categoria de patrimônio
   */
  async atualizarCategoria(id, dados) {
    const response = await api.put(`/categorias-patrimonio/${id}`, dados);
    return response.data;
  }

  /**
   * Excluir categoria de patrimônio
   */
  async excluirCategoria(id) {
    const response = await api.delete(`/categorias-patrimonio/${id}`);
    return response.data;
  }
}

const patrimonioService = new PatrimonioService();
export default patrimonioService;
